import { useCallback, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { AddCheckoutCartItemDocument } from "../../../../graphql/mutations/__generated__/checkout.mutation";
import {
  AddCheckoutCartItemInput,
  CheckoutConflictResolution,
} from "../../../../graphql/types";
import { isServerCheckoutEnabled } from "../../../../utils/featureFlags";
import { CheckoutCartConflict } from "./useCheckoutCart";

export type AddToCartArgs = Omit<AddCheckoutCartItemInput, "onConflict">;
export type AddToCartResult = "added" | "conflict" | "cancelled";

/**
 * Flag-aware add-to-cart for the legacy entry points
 * (docs/server-checkout-migration.md Phase 4). When `serverCheckout` is false
 * the caller runs its unchanged legacy Redux dispatch; when true it calls
 * `addItemToServerCart` and renders `ConflictModal` bound to `conflict` /
 * `resolveConflict` / `cancelConflict`. A conflict means the item was NOT
 * added; resolving re-calls the mutation with `onConflict` set.
 *
 * Deliberately mutation-only (no getCheckoutCart query) so legacy pages don't
 * fire cart requests while the flag is off.
 */
const useAddToCart = () => {
  const serverCheckout = isServerCheckoutEnabled();
  const [mutate, { loading: adding }] = useMutation(
    AddCheckoutCartItemDocument
  );
  const [conflict, setConflict] = useState<CheckoutCartConflict | null>(null);
  const pendingInput = useRef<AddToCartArgs | null>(null);

  const addItemToServerCart = useCallback(
    async (input: AddToCartArgs): Promise<AddToCartResult> => {
      const { data } = await mutate({
        variables: { input: { ...input, onConflict: null } },
      });
      const returnedConflict = data?.addCheckoutCartItem.conflict ?? null;
      if (returnedConflict) {
        pendingInput.current = input;
        setConflict(returnedConflict);
        return "conflict";
      }
      return "added";
    },
    [mutate]
  );

  const resolveConflict = useCallback(
    async (resolution: CheckoutConflictResolution): Promise<AddToCartResult> => {
      const input = pendingInput.current;
      setConflict(null);
      pendingInput.current = null;
      if (!input) return "cancelled";
      const { data } = await mutate({
        variables: { input: { ...input, onConflict: resolution } },
      });
      // An unsupported resolution returns the conflict again — treat as
      // cancelled rather than looping.
      return data?.addCheckoutCartItem.conflict ? "cancelled" : "added";
    },
    [mutate]
  );

  const cancelConflict = useCallback(() => {
    setConflict(null);
    pendingInput.current = null;
  }, []);

  return {
    serverCheckout,
    addItemToServerCart,
    conflict,
    resolveConflict,
    cancelConflict,
    adding,
  };
};

export default useAddToCart;
